/**
 * OppProjectCard.jsx - one opportunity/project rendered as a hero band plus a
 * card grid, the same layout the Projects view uses. An opportunity and a
 * project are the same record, so one component serves both.
 *
 * NOTE: the local fmtDate below intentionally shadows utils/format's - it
 * returns null for blanks and an unpadded m/d/yyyy. Do not swap it for the
 * shared one without checking every date on the card.
 */
import { Icon } from '../ui/Icon';
import { Pill } from '../ui/Pill';
import { fmt, fmtDate } from '../../utils/format';
import { leftoverOf } from '../../utils/projectMoney';
import { oldQuoteListValues } from '../opportunities/editOpportunityForm';
import { BTN_PRIMARY } from './crmStyles';

/* ===== Shared Opportunity/Project Card =====
 *
 * Renders a project (or opportunity — they're the same record type) using the
 * same hero-band + card-grid layout as the Projects view. Always shows an
 * Edit button. No phase or progress % anywhere (10/8/26: every row was
 * Proposal / 0); the old quote list's project #, tracking sheet and internal
 * name show in their own group, and only when the record has any.
 */
function OppProjectCard({ opp, onBack, accountName, onEdit }) {
  const p = opp;
  const fmtDate = (d) => {
    if (!d) return null;
    if (typeof d === 'string') {
      const m = d.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (m) return `${parseInt(m[2], 10)}/${parseInt(m[3], 10)}/${m[1]}`;
    }
    const dt = new Date(d);
    return isNaN(dt) ? d : dt.toLocaleDateString('en-US');
  };
  const price = parseFloat(p.quotedPrice || p.quoted_price) || 0;
  const labor = parseFloat(p.quotedLaborCost || p.quoted_labor_cost) || 0;
  const exp = parseFloat(p.quotedExpenses || p.quoted_expenses) || 0;
  const cogs = labor + exp;
  const profit = price - cogs;
  const margin = price > 0 && labor > 0 ? (profit / price) * 100 : null;
  const marginColor = margin === null ? '#aaa' : margin >= 30 ? '#1B5E20' : margin >= 20 ? '#E65100' : '#B71C1C';
  const leftover = leftoverOf(p); // derived: PO - invoiced (utils/projectMoney)
  const oldList = oldQuoteListValues(p);

  const Row = ({ label, value, mono, accent }) => {
    const has = value !== null && value !== undefined && value !== '' && value !== '-';
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          padding: '9px 0',
          borderBottom: '1px solid #F2F4EE',
          gap: 12,
        }}
      >
        <span style={{ fontSize: 11, color: '#777', fontWeight: 600, flexShrink: 0 }}>{label}</span>
        <span
          style={{
            fontSize: 12.5,
            color: has ? accent || '#0D0D0D' : '#bbb',
            fontWeight: has ? 700 : 500,
            textAlign: 'right',
            fontVariantNumeric: mono ? 'tabular-nums' : 'normal',
            fontFamily: mono ? "'JetBrains Mono', Consolas, monospace" : undefined,
            maxWidth: '60%',
            wordBreak: 'break-word',
          }}
        >
          {has ? value : '-'}
        </span>
      </div>
    );
  };

  const Section = ({ title, icon, children, accent = '#1B5E20' }) => (
    <div
      style={{
        background: '#fff',
        border: '1px solid #E5E9DD',
        borderRadius: 10,
        padding: '16px 18px',
        boxShadow: '0 1px 2px rgba(27,94,32,0.04)',
      }}
    >
      <div
        style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, paddingBottom: 8, borderBottom: `2px solid ${accent}` }}
      >
        <Icon name={icon} size={13} color={accent} />
        <span style={{ fontSize: 11, fontWeight: 800, color: accent, letterSpacing: 1.8, textTransform: 'uppercase' }}>{title}</span>
      </div>
      {children}
    </div>
  );

  return (
    <div>
      <div
        onClick={onBack}
        style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginBottom: 16, width: 'fit-content' }}
        onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.7')}
        onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
      >
        <div style={{ transform: 'rotate(180deg)', display: 'inline-flex' }}>
          <Icon name="chevron" size={16} color="#555" />
        </div>
        <span style={{ fontSize: 11, fontWeight: 700, color: '#555' }}>Back to {accountName}</span>
      </div>
      <div
        style={{
          background: '#fff',
          borderRadius: 12,
          border: '1px solid #ECECEC',
          boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
          overflow: 'hidden',
        }}
      >
        {/* HERO HEADER */}
        <div
          style={{ padding: '24px 32px', background: 'linear-gradient(135deg, #fff 0%, #F9FBF7 100%)', borderBottom: '1px solid #ECECEC' }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 24 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{ fontSize: 10, fontWeight: 800, color: '#1B5E20', letterSpacing: 1.5, textTransform: 'uppercase', marginBottom: 6 }}
              >
                {(p.status || '').toLowerCase() === 'won' ? 'Active Project' : 'Opportunity'} · {p.id}
              </div>
              <div style={{ fontSize: 22, fontWeight: 900, color: '#0D0D0D', marginBottom: 8, lineHeight: 1.2 }}>
                {p.name || p.description || 'Untitled'}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: '#1B5E20' }}>{p.customer || '-'}</span>
                {p.subCustomer && (
                  <>
                    <span style={{ fontSize: 11, color: '#ccc' }}>·</span>
                    <span style={{ fontSize: 11, fontWeight: 600, color: '#666' }}>{p.subCustomer}</span>
                  </>
                )}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
              <Pill label={p.status} />
              {onEdit && (
                <button
                  onClick={() => onEdit(p)}
                  style={{ ...BTN_PRIMARY, padding: '8px 16px', fontSize: 11, display: 'flex', alignItems: 'center', gap: 6 }}
                >
                  <Icon name="edit" size={11} color="#fff" /> Edit
                </button>
              )}
            </div>
          </div>

          {/* Key metrics band */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 10, marginTop: 20 }}>
            {[
              { label: 'Quoted Price', value: price ? fmt(price) : '-', color: '#0D0D0D' },
              { label: 'COGS', value: cogs ? fmt(cogs) : '-', color: '#B71C1C' },
              { label: 'Profit', value: profit && cogs ? fmt(profit) : '-', color: '#1B5E20' },
              { label: 'Margin', value: margin === null ? '-' : `${margin.toFixed(2)}%`, color: marginColor },
              {
                label: '$ Leftover',
                value: leftover != null ? fmt(leftover) : '-',
                color: leftover > 0 ? '#92400E' : '#1B5E20',
                bg: leftover > 0 ? '#FEF3C7' : leftover === 0 ? '#DCEDC8' : '#fff',
              },
            ].map((m) => (
              <div key={m.label} style={{ background: m.bg || '#fff', border: '1px solid #ECECEC', borderRadius: 8, padding: '10px 14px' }}>
                <div
                  style={{ fontSize: 9, fontWeight: 800, color: '#888', letterSpacing: 1.4, textTransform: 'uppercase', marginBottom: 4 }}
                >
                  {m.label}
                </div>
                <div style={{ fontSize: 17, fontWeight: 800, color: m.color, fontVariantNumeric: 'tabular-nums' }}>{m.value}</div>
              </div>
            ))}
          </div>
        </div>

        {/* CARD GRID */}
        <div style={{ padding: '24px 32px', background: '#FAFBF7' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 14 }}>
            <Section title="Identification" icon="briefcase">
              <Row label="Quote Date" value={fmtDate(p.quoteDate || p.quote_date)} mono />
              <Row label="Quote Letter" value={p.quoteLetter || p.quote_letter} mono />
              <Row label="Customer" value={p.customer} accent="#1B5E20" />
              <Row label="Sub Customer" value={p.subCustomer || p.sub_customer} />
              <Row label="Global Project #" value={p.globalProjectNumber || p.global_project_number} mono />
              <Row label="Division" value={p.division} />
              <Row label="Type" value={p.type} />
              <Row label="Billing Type" value={p.billingType || p.billing_type} />
            </Section>

            <Section title="People" icon="user" accent="#1565C0">
              <Row label="Approval Manager" value={p.approvalManager || p.approval_manager} accent="#1565C0" />
              <Row label="Project Manager" value={p.pm} />
              <Row label="Engineer" value={p.engineer} accent="#1B5E20" />
            </Section>

            <Section title="Financial Breakdown" icon="dollar">
              <Row label="Quoted Price" value={fmt(p.quotedPrice || p.quoted_price)} mono accent="#0D0D0D" />
              <Row label="PO Value" value={fmt(p.poValue || p.po_value)} mono />
              <Row label="Quoted Labor Cost" value={fmt(p.quotedLaborCost || p.quoted_labor_cost)} mono accent="#B71C1C" />
              <Row label="Quoted Expenses" value={fmt(p.quotedExpenses || p.quoted_expenses)} mono accent="#B71C1C" />
              <Row label="Quoted Profit" value={fmt(p.quotedProfit || p.quoted_profit)} mono accent="#1B5E20" />
              <Row
                label="Invoiced Already"
                value={p.invoicedAlready != null || p.invoiced_already != null ? fmt(p.invoicedAlready ?? p.invoiced_already) : null}
                mono
                accent="#1565C0"
              />
            </Section>

            <Section title="PO & Invoices" icon="briefcase" accent="#E65100">
              <Row label="PO Number" value={p.poNumber || p.po_number} mono />
              <Row label="PO Date Issued" value={fmtDate(p.poDateIssued || p.po_date_issued)} mono />
              <Row label="Latest Invoice Date" value={fmtDate(p.latestInvoiceDate || p.latest_invoice_date)} mono />
              <Row label="Invoice Numbers" value={p.invoiceNumbers || p.invoice_numbers} mono />
              <Row
                label="Quote Sent"
                value={p.quoteSent || p.quote_sent ? 'Yes' : 'No'}
                accent={p.quoteSent || p.quote_sent ? '#1B5E20' : '#888'}
              />
            </Section>

            <Section
              title="Status"
              icon="check"
              accent={(() => {
                const s = (p.status || '').toLowerCase();
                return s === 'won' ? '#1B5E20' : s === 'lost' ? '#B71C1C' : s === 'open' ? '#1565C0' : '#6A1B9A';
              })()}
            >
              <Row
                label="Status"
                value={p.status}
                accent={(() => {
                  const s = (p.status || '').toLowerCase();
                  return s === 'won' ? '#1B5E20' : s === 'lost' ? '#B71C1C' : s === 'open' ? '#1565C0' : '#6A1B9A';
                })()}
              />
              <Row label="Terms" value={p.terms} />
            </Section>

            {oldList.length > 0 && (
              <Section title="From the old quote list" icon="reporting" accent="#757575">
                {oldList.map((x) => (
                  <Row key={x.label} label={x.label} value={x.value} />
                ))}
              </Section>
            )}

            <Section title="Description" icon="reporting" accent="#757575">
              <div style={{ fontSize: 12, color: '#333', lineHeight: 1.55, padding: '4px 0' }}>
                {p.description || p.name || <span style={{ color: '#bbb' }}>No description.</span>}
              </div>
            </Section>
          </div>
        </div>
      </div>
    </div>
  );
}
export default OppProjectCard;
